// The smaller Copilot cards:
//   TestCallResult  an interviewer's test call: the checks with what each
//                   found, and the conversation it had (start, then all of it).
//   MemoryNotes     what Copilot was asked to remember (a note under the
//                   answer), and a default it learned, offered once as a card.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import styled from "styled-components";
import { toDisplayBrand } from "../../utils/displayBrand";
import { FONT, FIG_INK, FIG_MUTED, PRIMARY } from "../../design";
import { acceptMemoryApi, dismissMemoryApi } from "../../store/actions/operatorAction";
import {
  Card, CardTop, Kicker, CardTitle, Section, SectionLabel, CardFoot, Primary, Secondary, TextButton,
} from "./CardStyle";
import { DoneMark, FailMark, WarnMark } from "./CardIcons";

const Checks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
  li { display: flex; gap: 9px; align-items: flex-start; font-size: 12.5px; line-height: 1.45; color: ${FIG_INK}; }
  li svg { flex: none; margin-top: 1px; }
  .d { display: block; font-size: 11.5px; color: ${FIG_MUTED}; }
  .x { display: block; font-size: 11.5px; color: #7c3328; }
`;

const Talk = styled.div`
  background: #f7f7f9;
  border-radius: 10px;
  padding: 9px 11px;
  font-family: ${FONT};
  font-size: 12px;
  line-height: 1.5;
  color: ${FIG_INK};
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: ${(p) => (p.$all ? "320px" : "none")};
  overflow-y: auto;
  p { margin: 0; }
  b { font-weight: 600; }
`;

const Score = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 8px;
  b { font-size: 22px; font-weight: 700; color: ${FIG_INK}; }
  span { font-size: 12px; color: ${FIG_MUTED}; }
`;

/** One interviewer test call. `onRevise` puts a request in the box to edit. */
export function TestCallResult({ result, onRevise, when }) {
  const [all, setAll] = useState(false);
  const t = result && result.test_call;
  if (!t || !Array.isArray(t.checks)) return null;
  const failed = t.checks.filter((c) => !c.ok);
  const passed = t.checks.length - failed.length;
  const talk = all ? t.transcript || [] : (t.transcript || []).slice(0, 4);
  return (
    <Card>
      <CardTop>
        <div className="row">
          <Kicker $tone={failed.length ? "red" : "green"}>{failed.length ? "Did not pass" : "Passed"}</Kicker>
          <span className="meta">Test call{when ? ` · ${when}` : ""}{t.seconds ? ` · ${Math.round(t.seconds / 60) || 1} min` : ""}</span>
        </div>
        <CardTitle $big>{failed.length ? `${toDisplayBrand(t.agent_name || "The interviewer")} needs a fix before it takes calls` : `${toDisplayBrand(t.agent_name || "The interviewer")} is ready to take calls`}</CardTitle>
        <Score><b>{passed} of {t.checks.length}</b><span>checks passed{failed.length ? `, ${failed.length} to fix` : ""}</span></Score>
      </CardTop>
      <Section>
        <Checks>
          {t.checks.map((c, i) => (
            <li key={i}>
              {c.ok ? <DoneMark /> : <FailMark />}
              <span>{toDisplayBrand(c.label)}{c.detail && !c.ok ? <span className="x">{toDisplayBrand(c.detail)}</span> : null}</span>
            </li>
          ))}
          {(t.warnings || []).map((w, i) => (
            <li key={`w${i}`}><WarnMark /><span>To look at<span className="d">{toDisplayBrand(w)}</span></span></li>
          ))}
        </Checks>
      </Section>
      {talk.length ? (
        <Section>
          <SectionLabel>The conversation</SectionLabel>
          <Talk $all={all}>
            {talk.map((l, i) => <p key={i}><b>{l.speaker === "interviewer" ? "Interviewer" : "Respondent"}:</b> {toDisplayBrand(l.text)}</p>)}
          </Talk>
          {(t.transcript || []).length > 4 ? <TextButton type="button" onClick={() => setAll((v) => !v)}>{all ? "Show less" : "Read the whole test call"}</TextButton> : null}
        </Section>
      ) : null}
      {failed.length && onRevise ? (
        <CardFoot>
          <Primary type="button" onClick={() => onRevise(`The test call of ${t.agent_name || "the interviewer"} failed: ${failed.map((c) => c.label).join("; ")}. Fix it and test again.`)}>Fix and retest</Primary>
        </CardFoot>
      ) : null}
    </Card>
  );
}

const Note = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  max-width: 100%;
  font-family: ${FONT};
  font-size: 11.5px;
  line-height: 1.45;
  color: #4e4b48;
  background: #f3f2f1;
  border-radius: 10px;
  padding: 5px 10px;
  margin: 8px 6px 0 0;
  svg { flex: none; margin-top: 2px; }
  .txt { flex: 1; min-width: 0; }
  button { flex: none; border: 0; background: none; color: ${PRIMARY}; font-family: ${FONT}; font-size: 11.5px; font-weight: 600; padding: 0; cursor: pointer; }
`;

const Suggest = styled.div`
  font-family: ${FONT};
  border: 1px solid #d7ddf8;
  background: #f6f7fe;
  border-radius: 14px;
  padding: 12px 14px;
  margin: 10px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  .t { display: flex; gap: 10px; align-items: flex-start; }
  p { margin: 0; font-size: 13px; line-height: 1.5; color: ${FIG_INK}; }
  p span { display: block; font-size: 11.5px; color: #4e4b48; margin-top: 2px; }
  .acts { display: flex; gap: 8px; padding-left: 30px; flex-wrap: wrap; }
`;

const Bookmark = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M3 1.5h6v9L6 8.5 3 10.5z" /></svg>
);

/** Memory under an answer: notes for what was saved, and a suggestion to decide. */
export function MemoryNotes({ memory }) {
  const navigate = useNavigate();
  const [decided, setDecided] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!memory) return null;
  const s = memory.suggestion;
  const decide = async (yes) => {
    setBusy(true);
    try {
      await (yes ? acceptMemoryApi(s.id) : dismissMemoryApi(s.id));
      setDecided(yes ? "yes" : "no");
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || "That did not work." });
    } finally { setBusy(false); }
  };
  return (
    <>
      {(memory.saved || []).map((m) => (
        <Note key={m.id}><Bookmark /><span className="txt">Remembered: {toDisplayBrand(m.text)}</span><button type="button" onClick={() => navigate("/operator/memory")}>Edit</button></Note>
      ))}
      {s && decided ? (
        <Note><Bookmark /><span className="txt">{decided === "yes" ? `Saved as your default: ${toDisplayBrand(s.text)}` : "Not saved. Copilot will not suggest it again."}</span></Note>
      ) : s ? (
        <Suggest>
          <div className="t">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flex: "none" }}><circle cx="10" cy="10" r="9" fill="#E4E9FF" /><path d="M7 5.5h6v9L10 12.5 7 14.5z" stroke={PRIMARY} strokeWidth="1.5" /></svg>
            <p>Make this your default? {toDisplayBrand(s.text)}{s.evidence ? <span>{toDisplayBrand(s.evidence)}</span> : null}</p>
          </div>
          <div className="acts">
            <Primary type="button" disabled={busy} style={{ minHeight: 34, padding: "7px 12px", fontSize: 12.5 }} onClick={() => decide(true)}>Yes, remember it</Primary>
            <Secondary type="button" disabled={busy} style={{ minHeight: 34, padding: "7px 12px", fontSize: 12.5 }} onClick={() => decide(false)}>No</Secondary>
          </div>
        </Suggest>
      ) : null}
    </>
  );
}
